import { useEffect, useState } from 'react'
import JSZip from 'jszip'
import { DOMParser } from '@xmldom/xmldom'
import './TemplatePreviewModal.css'

interface TemplatePreviewModalProps {
  blob: Blob | null
  fileName: string
  onClose: () => void
}

export function TemplatePreviewModal({ blob, fileName, onClose }: TemplatePreviewModalProps) {
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (blob) {
      extractAndRenderContent(blob)
    }
  }, [blob])

  const extractAndRenderContent = async (blob: Blob) => {
    try {
      setIsLoading(true)
      setError(null)

      // Convert blob to array buffer
      const arrayBuffer = await blob.arrayBuffer()
      const bytes = new Uint8Array(arrayBuffer)

      // Load ZIP
      const zip = await JSZip.loadAsync(bytes)

      // Get content.xml
      const contentFile = zip.file('content.xml')
      if (!contentFile) {
        throw new Error('content.xml not found in document')
      }

      // Read and parse XML
      const contentXml = await contentFile.async('string')
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(contentXml, 'text/xml')

      // Extract text content from XML and convert to HTML
      const htmlContent = convertOdtXmlToHtml(xmlDoc)
      setPreviewHtml(htmlContent)
    } catch (err) {
      console.error('Error extracting content:', err)
      setError('Failed to extract document content. Please download the file to view it.')
    } finally {
      setIsLoading(false)
    }
  }

  const convertOdtXmlToHtml = (xmlDoc: Document): string => {
    // Extract text from OpenOffice XML structure
    // OpenOffice uses text:p for paragraphs, text:span for spans, etc.
    const body = xmlDoc.getElementsByTagName('office:body')[0]
    if (!body) return '<p>No content found</p>'

    let html = '<div class="odt-preview-content">'
    
    // Process all text paragraphs
    const paragraphs = body.getElementsByTagName('text:p')
    for (let i = 0; i < paragraphs.length; i++) {
      const para = paragraphs[i]
      const textContent = extractTextFromNode(para)
      if (textContent.trim()) {
        html += `<p>${escapeHtml(textContent)}</p>`
      }
    }

    // Process tables if any
    const tables = body.getElementsByTagName('table:table')
    for (let i = 0; i < tables.length; i++) {
      html += convertTableToHtml(tables[i])
    }

    html += '</div>'
    return html
  }

  const extractTextFromNode = (node: Node): string => {
    let text = ''
    if (node.nodeType === 3) { // TEXT_NODE
      text += node.nodeValue || ''
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        text += extractTextFromNode(node.childNodes[i])
      }
    }
    return text
  }

  const convertTableToHtml = (table: Element): string => {
    let html = '<table class="odt-table">'
    const rows = table.getElementsByTagName('table:table-row')
    for (let i = 0; i < rows.length; i++) {
      html += '<tr>'
      const cells = rows[i].getElementsByTagName('table:table-cell')
      for (let j = 0; j < cells.length; j++) {
        const cellText = extractTextFromNode(cells[j])
        html += `<td>${escapeHtml(cellText)}</td>`
      }
      html += '</tr>'
    }
    html += '</table>'
    return html
  }

  const escapeHtml = (text: string): string => {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  const handleDownload = () => {
    if (blob) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }

  return (
    <div className="template-preview-modal-overlay" onClick={onClose}>
      <div className="template-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="preview-modal-header">
          <h2>Template Preview</h2>
          <div className="preview-modal-actions">
            <button className="btn-secondary btn-sm" onClick={handleDownload}>
              Download
            </button>
            <button className="btn-secondary btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        
        <div className="preview-modal-content">
          {isLoading ? (
            <div className="preview-loading">Extracting document content...</div>
          ) : error ? (
            <div className="preview-error">
              <p>{error}</p>
              <button className="btn-primary" onClick={handleDownload}>
                Download Document
              </button>
            </div>
          ) : previewHtml ? (
            <div 
              className="preview-html-content"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <div className="preview-loading">No content to display</div>
          )}
        </div>
      </div>
    </div>
  )
}






























